"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssetPlayer = void 0;
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const common_1 = require("../device/common");
const DashJs_1 = require("../engines/DashJs");
const HlsJs_1 = require("../engines/HlsJs");
const Native_1 = require("../engines/Native");
const Shaka_1 = require("../engines/Shaka");
const helpers_1 = require("../utils/helpers");
const preferences_1 = require("../utils/preferences");
const BasePlayer_1 = require("./BasePlayer");
class AssetPlayer extends BasePlayer_1.BasePlayer {
    constructor(videoElement, subtitleContainer, instanceSettings, source) {
        var _a;
        super(videoElement, subtitleContainer, instanceSettings, source, {
            contentMarkers: ((_a = instanceSettings.assetMetadata) === null || _a === void 0 ? void 0 : _a.markerPoints) || [],
        });
        this.emittedContentMarkers = [];
    }
    /**
     * Get the preferred audio & text langauges for media playback.
     * Defaults to stored user preferences but fallsback to the player
     * locale when applicable.
     */
    getPreferredLanguages() {
        var _a, _b, _c, _d, _e;
        const userPreferences = (0, preferences_1.getPreferences)();
        const audioTracks = (_b = (_a = this.instanceSettings.assetMetadata) === null || _a === void 0 ? void 0 : _a.audioTracks) !== null && _b !== void 0 ? _b : [];
        const subtitles = (_d = (_c = this.instanceSettings.assetMetadata) === null || _c === void 0 ? void 0 : _c.subtitles) !== null && _d !== void 0 ? _d : [];
        const locale = this.instanceSettings.initOptions.locale;
        // if the users preferred audio/subtitle does not exist the logic below should act as if there was no preference.
        let subtitle = userPreferences.subtitle &&
            // if the preferences contain a subtitle preference without any language
            // it means that the user have disabled subtitles.
            (!userPreferences.subtitle.language ||
                !subtitles.length || // if we have no subtitles to test against we let the engine decide.
                subtitles.includes(userPreferences.subtitle.language))
            ? userPreferences.subtitle
            : undefined, audio = ((_e = userPreferences.audio) === null || _e === void 0 ? void 0 : _e.language) &&
            (!audioTracks.length || // if we have no audio tracks to test against we let the engine decide.
                audioTracks.includes(userPreferences.audio.language))
            ? userPreferences.audio
            : undefined;
        if (locale) {
            // if there is no audio preference AND the value of `locale` exist as an audioTrack
            // set the preferred language to `locale`.
            if (!audio && audioTracks.includes(locale)) {
                audio = { language: locale, kind: "main" };
            }
            // if there is no subtitle preference AND the `locale` exist as a subtitle AND there is no preferred audio OR the
            // preferred audio is NOT the same language as `locale`` then set the preferred subtitle language to `locale`.
            if (!subtitle &&
                subtitles.includes(locale) &&
                (!audio || audio.language !== locale)) {
                subtitle = { language: locale, kind: "subtitles" };
            }
        }
        return {
            audio,
            subtitle,
        };
    }
    getPlayerEngine() {
        var _a;
        const { format, mediaLocator, drm, liveDelay } = this.getFormat();
        this.mediaLocator = mediaLocator;
        this.license = drm;
        this.playbackFormat = format;
        if (process.env.NODE_ENV === "development") {
            const devEngine = this.getPlayerEngineDev();
            if (devEngine) {
                return devEngine;
            }
        }
        const bitmovinEngine = this.createBitmovinEngine();
        if (bitmovinEngine) {
            return bitmovinEngine;
        }
        switch (format) {
            case "HLS":
                if (((_a = this.instanceSettings.mediaInfo) === null || _a === void 0 ? void 0 : _a.isLive) &&
                    !this.license &&
                    liveDelay &&
                    (0, helpers_1.isMSESupported)()) {
                    return new HlsJs_1.HlsJs(this.videoElement, this.instanceSettings);
                }
                if ((0, helpers_1.isNativeHlsSupported)()) {
                    return new Native_1.Native(this.videoElement, this.instanceSettings);
                }
                return new HlsJs_1.HlsJs(this.videoElement, this.instanceSettings);
            case "DASH":
                if ((0, common_1.isLGTV)() || (0, common_1.getBrowser)() === "firefox") {
                    return new DashJs_1.DashJs(this.videoElement, this.instanceSettings);
                }
                return new Shaka_1.Shaka(this.videoElement, this.instanceSettings);
            case "SMOOTHSTREAMING":
                return new DashJs_1.DashJs(this.videoElement, this.instanceSettings);
            case "MP4":
            case "MP3":
                return new Native_1.Native(this.videoElement, this.instanceSettings);
            default:
                throw new js_player_shared_1.InitError(`Support for ${format} not yet implemented`, {
                    type: js_player_shared_1.ErrorTypes.OPTIONS,
                });
        }
    }
    checkifActiveMarker(contentMarker, markerId, startEvent, endEvent) {
        const { offset, endOffset } = contentMarker;
        const isAlreadyEmitted = this.emittedContentMarkers.includes(markerId);
        const currentTime = this.getCurrentTime() * 1000;
        if (!isAlreadyEmitted &&
            endOffset &&
            currentTime > offset &&
            currentTime < endOffset) {
            this.emit(startEvent, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { contentMarker }));
            this.emittedContentMarkers.push(markerId);
        }
        else if (isAlreadyEmitted &&
            endOffset &&
            endEvent &&
            (currentTime > endOffset || currentTime < offset)) {
            this.emit(endEvent, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { contentMarker }));
            this.emittedContentMarkers = this.emittedContentMarkers.filter((ecm) => ecm !== markerId);
        }
        else if (!isAlreadyEmitted && !endOffset && currentTime > offset) {
            //you are markerPoint or credit
            this.emittedContentMarkers.push(markerId);
            this.emit(startEvent, Object.assign(Object.assign({}, this.getDefaultPlayerEvent()), { contentMarker }));
        }
        else if (isAlreadyEmitted && !endOffset && currentTime < offset) {
            this.emittedContentMarkers = this.emittedContentMarkers.filter((ecm) => ecm !== markerId);
        }
    }
    evaluateContentMarkers() {
        const contentMarkers = this.getContentMarkers();
        contentMarkers === null || contentMarkers === void 0 ? void 0 : contentMarkers.forEach((contentMarker, i) => {
            const markerId = `${contentMarker.type}_${i}`;
            switch (contentMarker.type) {
                case "INTRO":
                    this.checkifActiveMarker(contentMarker, markerId, js_player_shared_1.PlayerEvents.INTRO_START, js_player_shared_1.PlayerEvents.INTRO_END);
                    break;
                case "CHAPTER":
                    this.checkifActiveMarker(contentMarker, markerId, js_player_shared_1.PlayerEvents.CHAPTER_START, js_player_shared_1.PlayerEvents.CHAPTER_END);
                    break;
                default:
                    this.checkifActiveMarker(contentMarker, markerId, js_player_shared_1.PlayerEvents.MARKER);
                    break;
            }
        });
    }
    onTimeUpdate() {
        super.onTimeUpdate();
        this.evaluateContentMarkers();
    }
    getFormat() {
        const availableFormats = this.instanceSettings.formats || [];
        const preferredFormats = this.instanceSettings.initOptions.preferredFormats;
        const supportedFormatTypes = (0, helpers_1.getSupportedFormats)();
        const supportedFormats = availableFormats.filter(({ format }) => supportedFormatTypes.includes(format));
        const isDrm = this.isDrm(availableFormats);
        if (isDrm && !this.instanceSettings.supportedKeySystem) {
            const errMessage = "Asset is DRM, though no supported key systems reported by the browser";
            const noSupportedKeySystemError = new js_player_shared_1.InitError(errMessage, {
                type: js_player_shared_1.ErrorTypes.DRM,
            });
            this.emit(js_player_shared_1.PlayerEvents.ERROR, noSupportedKeySystemError);
            throw noSupportedKeySystemError;
        }
        const decider = isDrm
            ? this.decideEncryptedFormatToPlay.bind(this)
            : this.decideNonEncryptedFormatToPlay.bind(this);
        let format = decider(supportedFormats, preferredFormats);
        if (!format && supportedFormats.length > 0) {
            format = supportedFormats[0];
        }
        if (!format) {
            const errMsg = `Could not match requested formats: ${preferredFormats}`;
            throw new js_player_shared_1.APIError(errMsg, {
                type: js_player_shared_1.ErrorTypes.API_PLAY_REQUEST,
            });
        }
        return format;
    }
    decideEncryptedFormatToPlay(formats, preferredFormats = []) {
        var _a;
        let format;
        const preferredKeysystem = (_a = this.instanceSettings.initOptions) === null || _a === void 0 ? void 0 : _a.keysystem;
        if (preferredKeysystem) {
            format = formats.find((f) => {
                var _a;
                return Object.keys((_a = f.drm) !== null && _a !== void 0 ? _a : {}).find((k) => k === preferredKeysystem);
            });
        }
        else {
            for (const preferredFormat of preferredFormats) {
                format = formats.find((f) => {
                    if (f.drm && (0, helpers_1.isNativeHlsSupported)()) {
                        return f.format === "HLS";
                    }
                    if (f.drm && !(0, helpers_1.isNativeHlsSupported)()) {
                        return (f.format === preferredFormat.toUpperCase() && f.format !== "HLS");
                    }
                });
                if (format) {
                    break;
                }
            }
        }
        return format;
    }
    decideNonEncryptedFormatToPlay(formats, preferredFormats = []) {
        let format;
        for (const preferredFormat of preferredFormats) {
            format = formats.find((f) => {
                return f.format === preferredFormat.toUpperCase();
            });
            if (format) {
                break;
            }
        }
        return format;
    }
    isDrm(formats) {
        return !!formats.find((f) => f.drm);
    }
    isSeekable() {
        var _a;
        const contractRestrictions = (_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions;
        if (contractRestrictions) {
            const { timeshiftEnabled, ffEnabled, rwEnabled } = contractRestrictions;
            return timeshiftEnabled === false ||
                (rwEnabled === false && ffEnabled === false)
                ? false
                : super.isSeekable();
        }
        return super.isSeekable();
    }
}
exports.AssetPlayer = AssetPlayer;
//# sourceMappingURL=AssetPlayer.js.map